/**
 * TEMPORARY instrumentation for the freeze between a render landing and the
 * picture appearing. Remove once the culprit is known.
 *
 * Three things it has to get right, each learned the hard way:
 *
 * - **Wall time does not answer the question.** A phase may take two seconds
 *   with the main thread free (a worker, a network read), which is fine, or two
 *   hundred milliseconds with it blocked, which is the freeze. A 4 ms ticker
 *   runs alongside and reports the longest gap it saw.
 * - **The two passes overlap.** The preview's tail runs after the full pass has
 *   already started, so a single timeline let a stale `perfEnd` close the run
 *   that had just opened — and every mark of the expensive pass was dropped.
 *   Each run carries a generation, and a tail belonging to an older one is
 *   ignored.
 * - **The freeze can land after the last mark.** React commits from its own
 *   scheduler, which need not have run by the animation frame after a dispatch,
 *   and a GC pause lands wherever it likes. So a `longtask` observer keeps
 *   reporting for a while after the run, with each task placed relative to the
 *   dispatch, and the heap is printed alongside.
 */

const TICK_MS = 4;

/** A gap this much over the interval is the thread being busy, not timer drift. */
const NOISE_MS = 4;

/** How long after a run to keep reporting long tasks it may have caused. */
const TAIL_MS = 8000;

/** Chrome's non-standard heap readout; absent elsewhere, and that is fine. */
type MaybeMemory = {
  memory?: { usedJSHeapSize: number; totalJSHeapSize: number };
};

function heapMB(): string {
  const used = (performance as Performance & MaybeMemory).memory
    ?.usedJSHeapSize;

  return used === undefined ? '' : ` [heap ${(used / 1e6).toFixed(0)}MB]`;
}

let timer: ReturnType<typeof setTimeout> | undefined;

let lastTick = 0;

let maxBlock = 0;

let phaseStart = 0;

let runStart = 0;

/** Which run is current; a tail from an older one must not close this one. */
let generation = 0;

let running = false;

let observer: PerformanceObserver | undefined;

function tick(): void {
  const now = performance.now();

  const gap = now - lastTick - TICK_MS;

  if (gap > maxBlock) {
    maxBlock = gap;
  }

  lastTick = now;

  timer = setTimeout(tick, TICK_MS);
}

/**
 * Reports every long task for a while, placed relative to the run that is
 * open. This is what catches a freeze that lands after the last mark — a React
 * commit from its own scheduler, or a GC pause.
 */
function watchLongTasks(): void {
  if (observer || typeof PerformanceObserver !== 'function') {
    return;
  }

  try {
    observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        console.log(
          `[panorama-perf] ⚠ long task ${entry.duration.toFixed(0)}ms at +${(
            entry.startTime - runStart
          ).toFixed(0)}ms from run start${heapMB()}`,
        );
      }
    });

    observer.observe({ entryTypes: ['longtask'] });
  } catch {
    // Firefox has no `longtask`; the ticker above still reports the blocking.
    observer = undefined;
  }
}

/** Opens a timeline. Each {@link perfMark} closes the phase before it. */
export function perfStart(label: string): void {
  generation++;

  runStart = phaseStart = lastTick = performance.now();

  maxBlock = 0;

  running = true;

  clearTimeout(timer);

  timer = setTimeout(tick, TICK_MS);

  watchLongTasks();

  console.log(`[panorama-perf] ── ${label}${heapMB()}`);
}

/** Closes the phase that just ran and opens the next. */
export function perfMark(label: string): void {
  if (!running) {
    return;
  }

  const now = performance.now();

  const blocked =
    maxBlock > NOISE_MS ? `, blocked ${maxBlock.toFixed(0)}ms` : '';

  console.log(
    `[panorama-perf] ${label}: ${(now - phaseStart).toFixed(0)}ms${blocked}${heapMB()}`,
  );

  phaseStart = now;

  maxBlock = 0;
}

/**
 * Closes the timeline after the paint, then keeps watching for long tasks the
 * run may still be paying for. A tail belonging to a superseded run is dropped:
 * the preview's tail arrives once the full pass is already under way, and
 * closing the run there is what silenced the pass we actually wanted.
 */
export function perfEnd(label: string): void {
  if (!running) {
    return;
  }

  const mine = generation;

  // React commits from its own scheduler, which drives it off a `MessageChannel`
  // task. A message posted now therefore runs after that work, which splits the
  // wait in two: what React did, and what the browser then did to put it on
  // screen. They want completely different fixes.
  const channel = new MessageChannel();

  channel.port1.onmessage = () => {
    if (mine === generation) {
      perfMark(`${label} — React work`);
    }

    channel.port1.close();
  };

  channel.port2.postMessage(null);

  requestAnimationFrame(() => {
    setTimeout(() => {
      if (mine !== generation) {
        return;
      }

      perfMark(`${label} — browser layout + paint`);

      console.log(
        `[panorama-perf] ══ marks done, ${(
          performance.now() - runStart
        ).toFixed(0)}ms${heapMB()} — watching for late long tasks`,
      );

      setTimeout(() => {
        if (mine !== generation) {
          return;
        }

        running = false;

        clearTimeout(timer);

        observer?.disconnect();

        observer = undefined;

        console.log(`[panorama-perf] ══ done${heapMB()}`);
      }, TAIL_MS);
    }, 0);
  });
}

/** Times one synchronous step without opening a phase of its own. */
export function perfSync<T>(label: string, fn: () => T): T {
  if (!running) {
    return fn();
  }

  const at = performance.now();

  const result = fn();

  console.log(
    `[panorama-perf]   · ${label}: ${(performance.now() - at).toFixed(0)}ms (sync)`,
  );

  return result;
}
